import { useState, useEffect } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, ShoppingBag, Star, Heart, Users, Settings, LogOut, Grape,
  Activity, FileText, Sun, Moon, ShieldAlert, Package, Radar, DollarSign,
  Menu, X, Truck,
} from 'lucide-react';

const standardNav = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/orders', icon: ShoppingBag, label: 'Bestellungen' },
  { to: '/delivery-status', icon: Truck, label: 'Delivery Status' },
  { to: '/reviews', icon: Star, label: 'Reviews' },
  { to: '/refunds', icon: DollarSign, label: 'Erstattungen' },
  { to: '/wishlist', icon: Heart, label: 'Wishlist' },
  { to: '/agents', icon: Users, label: 'Agenten' },
  { to: '/invoices', icon: FileText, label: 'Rechnungen' },
  { to: '/settings', icon: Settings, label: 'Einstellungen' },
  { to: '/products', icon: Package, label: 'Alle Artikel' },
  { to: '/scanner-monitor', icon: Radar, label: 'Scanner Monitor' },
];

const deliveryNav = [
  { to: '/delivery-status', icon: Truck, label: 'Delivery Status' },
];

function readStoredUser() {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null');
  } catch {
    return null;
  }
}

export default function Layout() {
  const [light, setLight] = useState(localStorage.getItem('theme') === 'light');
  const [user, setUser] = useState(readStoredUser());
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isDeliveryUser = user?.role === 'delivery_only';
  const isAdmin = user?.plan === 'admin' && !isDeliveryUser;
  const canUseScannerMonitor = !isDeliveryUser && (user?.plan === 'admin' || user?.plan === 'mirror');
  const nav = isDeliveryUser
    ? deliveryNav
    : standardNav.filter((item) => item.to !== '/scanner-monitor' || canUseScannerMonitor);

  useEffect(() => {
    const saved = localStorage.getItem('theme');
    document.body.setAttribute('data-theme', saved === 'light' ? 'light' : '');
    document.documentElement.setAttribute('data-theme', saved === 'light' ? 'light' : '');
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    fetch('/auth/me', { headers: { Authorization: `Bearer ${token}` } })
      .then(async (response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.json();
      })
      .then((data) => {
        setUser(data);
        localStorage.setItem('user', JSON.stringify(data));
        if (data.role === 'delivery_only' && location.pathname !== '/delivery-status') {
          navigate('/delivery-status', { replace: true });
        }
      })
      .catch((err) => {
        if (err.message === '401') {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
      });
  }, [location.pathname, navigate]);

  const toggleTheme = () => {
    const next = !light;
    setLight(next);
    document.body.setAttribute('data-theme', next ? 'light' : '');
    document.documentElement.setAttribute('data-theme', next ? 'light' : '');
    localStorage.setItem('theme', next ? 'light' : 'dark');
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  const navItemStyle = (isActive) => ({
    display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
    borderRadius: 8, marginBottom: 2, textDecoration: 'none',
    color: isActive ? 'white' : 'var(--text2)',
    background: isActive ? 'var(--accent)' : 'transparent',
    fontFamily: 'Syne', fontWeight: isActive ? 600 : 400, fontSize: 13,
    transition: 'all 0.15s',
  });

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <button
        onClick={() => setSidebarOpen(true)}
        style={{ position: 'fixed', top: 16, left: 16, zIndex: 999, display: 'none', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: 8, cursor: 'pointer', color: 'var(--text)' }}
        className="mobile-menu-btn"
        aria-label="Menü öffnen"
      >
        <Menu size={20} />
      </button>

      {sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'none' }} className="mobile-overlay" />
      )}

      <aside
        style={{ width: 220, background: 'var(--bg2)', borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 1001, transform: sidebarOpen ? 'translateX(0)' : 'translateX(0)', transition: 'transform 0.3s ease' }}
        className="sidebar"
      >
        <button onClick={() => setSidebarOpen(false)} style={{ position: 'absolute', top: 20, right: 16, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', display: 'none' }} className="mobile-close-btn" aria-label="Menü schließen">
          <X size={20} />
        </button>

        <div style={{ padding: '24px 20px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ background: 'var(--accent)', borderRadius: 10, padding: 8, display: 'flex' }}>
              <Grape size={18} color="white" />
            </div>
            <div>
              <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 15, color: 'var(--text)' }}>Vine Scanner</div>
              <div style={{ fontSize: 10, color: 'var(--text3)' }}>{isDeliveryUser ? `DELIVERY · ${user?.operatorCode || ''}` : 'PRO DASHBOARD'}</div>
            </div>
          </div>
        </div>

        <nav style={{ flex: 1, padding: '16px 12px', overflowY: 'auto' }}>
          {nav.map(({ to, icon: Icon, label }) => (
            <NavLink key={to} to={to} end={to === '/'} style={({ isActive }) => navItemStyle(isActive)} onClick={() => setSidebarOpen(false)}>
              <Icon size={16} />
              {label}
            </NavLink>
          ))}

          {isAdmin && (
            <>
              <div style={{ fontSize: 10, color: 'var(--text3)', padding: '12px 12px 4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Administration</div>
              <NavLink
                to="/admin"
                style={({ isActive }) => ({ ...navItemStyle(isActive), background: isActive ? '#b71c1c' : 'rgba(183,28,28,0.1)', color: isActive ? 'white' : '#ef9a9a' })}
                onClick={() => setSidebarOpen(false)}
              >
                <ShieldAlert size={16} />
                Admin Panel
              </NavLink>
            </>
          )}
        </nav>

        <div style={{ padding: '16px 12px', borderTop: '1px solid var(--border)' }}>
          {canUseScannerMonitor && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', marginBottom: 8, borderRadius: 8, background: 'rgba(74,222,128,0.08)' }}>
              <Activity size={12} color="var(--accent2)" />
              <span style={{ fontSize: 11, color: 'var(--accent2)' }}>Scanner aktiv</span>
            </div>
          )}

          <button onClick={toggleTheme} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '9px 12px', borderRadius: 8, border: 'none', background: 'transparent', color: 'var(--text3)', cursor: 'pointer', fontSize: 13, fontFamily: 'Syne', marginBottom: 4 }}>
            {light ? <Moon size={16} /> : <Sun size={16} />}
            {light ? 'Dark Mode' : 'Light Mode'}
          </button>

          <button onClick={logout} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '9px 12px', borderRadius: 8, border: 'none', background: 'transparent', color: 'var(--text3)', cursor: 'pointer', fontSize: 13, fontFamily: 'Syne' }}>
            <LogOut size={16} />
            Abmelden
          </button>
        </div>
      </aside>

      <main style={{ marginLeft: 220, flex: 1, minHeight: '100vh' }} className="main-content">
        <Outlet />
      </main>

      <style>{`
        @media (max-width: 768px) {
          .mobile-menu-btn { display: block !important; }
          .mobile-overlay { display: block !important; }
          .mobile-close-btn { display: block !important; }
          .sidebar { transform: ${sidebarOpen ? 'translateX(0)' : 'translateX(-100%)'} !important; }
          .main-content { margin-left: 0 !important; }
        }
      `}</style>
    </div>
  );
}
